import React from 'react';
import { Card, Row, Col, Tag, Progress, Empty } from 'antd';
import {
  BarChartOutlined,
  ThunderboltOutlined,
  FileTextOutlined,
  SafetyOutlined,
  AimOutlined
} from '@ant-design/icons';

/**
 * 机审场景统计数据展示组件
 * 用于在预览弹窗中展示机审场景的指标统计数据
 * 支持 presetMetrics + customMetrics 数据结构
 */
function AutoReviewStatsDisplay({ data, title }) {
  if (!data || Object.keys(data).length === 0) {
    return (
      <Empty
        description="暂无统计数据"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // 转换 presetMetrics 数据格式：兼容多种数据结构
  const transformPresetMetrics = (data) => {
    if (!data || !data.presetMetrics) return data;

    const transformed = { ...data };
    const newPresetMetrics = { ...data.presetMetrics };

    // 遍历所有维度
    Object.entries(newPresetMetrics).forEach(([dimensionKey, dimensionData]) => {
      // 跳过预设维度，保持原样
      if (['document', 'rule_avg', 'rule_detail', 'group_metrics'].includes(dimensionKey)) {
        return;
      }

      // 安全检查：确保 dimensionData 是对象
      if (!dimensionData || typeof dimensionData !== 'object') {
        return;
      }

      // 判断是否需要转换：只有当存在 instance_stats 数组且长度 > 0 时才处理
      const hasInstanceStats = Array.isArray(dimensionData.instance_stats) && 
                               dimensionData.instance_stats.length > 0;

      if (hasInstanceStats) {
        // 格式 1：instance_stats 数组结构（新格式）
        // 保留原结构，同时添加维度级汇总字段
        const firstInstance = dimensionData.instance_stats[0];
        
        // 安全检查：确保 firstInstance 是对象
        if (!firstInstance || typeof firstInstance !== 'object') {
          return;
        }

        // 保留完整的 instance_stats 数组，同时添加维度级字段
        // 注意：不要覆盖维度级别的 accuracy 等字段，它们才是整体汇总值
        newPresetMetrics[dimensionKey] = {
          // 保留完整的 instance_stats 数组（重要！）
          instance_stats: dimensionData.instance_stats,
          // 保留维度级元数据
          instance_count: dimensionData.instance_count,
          total: dimensionData.total,
          match_total: dimensionData.match_total,
          // 添加维度级 rule_stats（如果有的话）
          rule_stats: dimensionData.rule_stats || [],
          // 保留维度级别的指标值（这些是整体汇总值，不能被第一个instance覆盖）
          accuracy: dimensionData.accuracy,
          accuracy_value: dimensionData.accuracy_value,
          risk_precision: dimensionData.risk_precision,
          risk_precision_value: dimensionData.risk_precision_value,
          no_risk_precision: dimensionData.no_risk_precision,
          no_risk_precision_value: dimensionData.no_risk_precision_value,
          risk_recall: dimensionData.risk_recall,
          risk_recall_value: dimensionData.risk_recall_value,
          no_risk_recall: dimensionData.no_risk_recall,
          no_risk_recall_value: dimensionData.no_risk_recall_value,
          // 兼容旧代码：如果维度级别没有这些字段，才从第一个instance取
          instance_name: dimensionData.instance_name || firstInstance.instance_name,
          instance_code: dimensionData.instance_code || firstInstance.instance_code,
          rule_avg: dimensionData.rule_avg || firstInstance.rule_avg
        };
      }
      // 否则（格式 2、3）：已经是扁平化结构或空对象，不需要转换，保持原样
    });

    transformed.presetMetrics = newPresetMetrics;
    return transformed;
  };

  // 转换数据
  const transformedData = transformPresetMetrics(data);
  const statsData = transformedData;

  // 颜色配置
  const GRADIENT_COLORS = [
    { start: '#667eea', end: '#764ba2' },
    { start: '#4facfe', end: '#00f2fe' },
    { start: '#43e97b', end: '#38f9d7' },
    { start: '#fa709a', end: '#fee140' },
    { start: '#f093fb', end: '#f5576c' },
    { start: '#30cfd0', end: '#330867' }
  ];

  const COLORS = ['#5B8FF9', '#5AD8A6', '#5D7092', '#F6BD16', '#E86452', '#6DC8EC', '#945FB9', '#FF9845'];

  // 根据值获取颜色
  const getColorByRate = (rate) => {
    const numericRate = typeof rate === 'string' ? parseFloat(rate.replace('%', '')) / 100 : rate;
    if (numericRate >= 0.9) return '#52c41a';
    if (numericRate >= 0.75) return '#1890FF';
    if (numericRate >= 0.6) return '#faad14';
    return '#ff4d4f';
  };

  // 预设指标名称映射
  const PRESET_METRIC_NAMES = {
    risk_precision: '有风险精确率',
    no_risk_precision: '无风险精确率',
    risk_recall: '有风险召回率',
    no_risk_recall: '无风险召回率',
    accuracy: '准确率'
  };

  // 渲染指标卡片
  const MetricCard = ({ name, value, numerator, denominator, index, dimension }) => {
    const gradient = GRADIENT_COLORS[index % GRADIENT_COLORS.length];
    const numericValue = typeof value === 'string' ? parseFloat(value) / 100 : value;
    
    return (
      <Card 
        style={{
          background: `linear-gradient(135deg, ${gradient.start} 0%, ${gradient.end} 100%)`,
          border: 'none',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          height: '100%'
        }}
        bodyStyle={{ padding: '24px' }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-start justify-between mb-3">
            <Tag color="rgba(255,255,255,0.3)" style={{ 
              border: '1px solid rgba(255,255,255,0.5)',
              color: 'white',
              fontWeight: 500
            }}>
              {dimension}
            </Tag>
            <BarChartOutlined style={{ fontSize: '20px', color: 'rgba(255,255,255,0.8)' }} />
          </div>
          
          <div className="flex-1">
            <div style={{ color: 'white', fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
              {name}
            </div>
            <div style={{ color: 'white', fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
              {typeof value === 'string' ? value : `${(numericValue * 100).toFixed(2)}%`}
            </div>
            {numerator !== undefined && denominator !== undefined && (
              <div style={{ color: 'white', fontSize: '12px', opacity: 0.8 }}>
                {numerator} / {denominator}
              </div>
            )}
          </div>
          
          <div style={{ marginTop: '16px' }}>
            <Progress 
              percent={numericValue * 100} 
              strokeColor="rgba(255,255,255,0.9)"
              trailColor="rgba(255,255,255,0.2)"
              showInfo={false}
              strokeWidth={6}
            />
          </div>
        </div>
      </Card>
    );
  };

  // 渲染维度指标
  const renderDimensionMetrics = (dimensionData, dimensionKey, dimensionName) => {
    if (!dimensionData) return null;

    const metrics = Object.entries(PRESET_METRIC_NAMES).map(([key, name], index) => {
      const value = dimensionData[key];
      const valueNum = dimensionData[`${key}_value`];
      if (value === undefined && valueNum === undefined) return null;
      
      return {
        key,
        name,
        value: value || `${(valueNum * 100).toFixed(2)}%`,
        numericValue: valueNum || (typeof value === 'string' ? parseFloat(value) / 100 : value),
        index
      };
    }).filter(Boolean);

    // 修改：即使没有指标字段，如果有rule_stats也要渲染
    if (metrics.length === 0 && !dimensionData.rule_stats) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        {/* 只有当有指标时才渲染维度标题和指标卡片 */}
        {metrics.length > 0 && (
          <>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
              {dimensionKey === 'document' ? '📊' : dimensionKey === 'rule_avg' ? '🎯' : '📋'} {dimensionName}指标
            </h2>
            
            {dimensionKey === 'rule_avg' && dimensionData.rule_count && (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '16px 24px',
                borderRadius: '12px',
                marginBottom: '16px',
                color: 'white'
              }}>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                  💡 维度说明
                </div>
                <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                  以（单据 × 规则）为最小评估单元，各规则指标的平均值。
                  共 {dimensionData.rule_count} 条规则参与计算。
                </div>
              </div>
            )}

            <Row gutter={[16, 16]}>
              {metrics.map((metric, idx) => (
                <Col span={6} key={metric.key}>
                  <MetricCard 
                    name={metric.name}
                    value={metric.value}
                    index={idx + (dimensionKey === 'rule_avg' ? 5 : dimensionKey === 'rule_detail' ? 10 : 0)}
                    dimension={dimensionName}
                  />
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* 规则平均维度的规则明细统计 */}
        {dimensionKey === 'rule_avg' && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 维度说明
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                以（单据 × 规则）为最小评估单元，各规则指标的平均值。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则参与计算。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    
                    if (rule.risk_precision) {
                      metricCards.push(
                        <div key="risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>{rule.risk_precision}</div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_precision) {
                      metricCards.push(
                        <div key="no_risk_precision" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>{rule.no_risk_precision}</div>
                        </div>
                      );
                    }
                    
                    if (rule.risk_recall) {
                      metricCards.push(
                        <div key="risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>{rule.risk_recall}</div>
                        </div>
                      );
                    }
                    
                    if (rule.no_risk_recall) {
                      metricCards.push(
                        <div key="no_risk_recall" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>{rule.no_risk_recall}</div>
                        </div>
                      );
                    }
                    
                    if (rule.accuracy) {
                      metricCards.push(
                        <div key="accuracy" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>{rule.accuracy}</div>
                        </div>
                      );
                    }
                    
                    if (metricCards.length === 0) return null;
                    
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className="grid gap-3" style={{ 
                        fontSize: '13px',
                        gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)`
                      }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    if (rule.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>);
                    if (rule.machine_risky !== undefined) stats.push(<span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>);
                    if (rule.machine_non_risky !== undefined) stats.push(<span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>);
                    if (rule.expected_risky !== undefined) stats.push(<span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>);
                    if (rule.expected_non_risky !== undefined) stats.push(<span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>);
                    
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 规则明细维度的规则统计 */}
        {dimensionKey === 'rule_detail' && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 维度说明
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                按规则明细维度统计，展示每个规则的具体指标表现。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    if (rule.risk_precision) metricCards.push(<div key="rp" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>{rule.risk_precision}</div></div>);
                    if (rule.no_risk_precision) metricCards.push(<div key="nrp" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>{rule.no_risk_precision}</div></div>);
                    if (rule.risk_recall) metricCards.push(<div key="rr" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>{rule.risk_recall}</div></div>);
                    if (rule.no_risk_recall) metricCards.push(<div key="nrr" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>{rule.no_risk_recall}</div></div>);
                    if (rule.accuracy) metricCards.push(<div key="acc" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>{rule.accuracy}</div></div>);
                    
                    if (metricCards.length === 0) return null;
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className="grid gap-3" style={{ fontSize: '13px', gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)` }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    if (rule.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>);
                    if (rule.machine_risky !== undefined) stats.push(<span key="mr">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>);
                    if (rule.machine_non_risky !== undefined) stats.push(<span key="mnr">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>);
                    if (rule.expected_risky !== undefined) stats.push(<span key="er">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>);
                    if (rule.expected_non_risky !== undefined) stats.push(<span key="enr">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>);
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 自定义维度的规则统计 */}
        {!['document', 'rule_avg', 'rule_detail'].includes(dimensionKey) && dimensionData.rule_stats && dimensionData.rule_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>规则详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            <div style={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              padding: '16px 24px',
              borderRadius: '12px',
              marginBottom: '16px',
              color: 'white'
            }}>
              <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                💡 规则统计
              </div>
              <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                展示该维度下各规则的详细指标表现。
                {dimensionData.rule_count && ` 共 ${dimensionData.rule_count} 条规则。`}
              </div>
            </div>
            
            <div className="space-y-4">
              {dimensionData.rule_stats.map((rule, index) => (
                <div 
                  key={rule.rule_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {rule.rule_name || rule.rule_code}
                      </span>
                    </div>
                    {rule.accuracy && (
                      <span style={{ 
                        fontSize: '24px', 
                        fontWeight: 'bold', 
                        color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                      }}>
                        {rule.accuracy}
                      </span>
                    )}
                  </div>
                  
                  {/* 动态展示存在的指标卡片 */}
                  {(() => {
                    const metricCards = [];
                    if (rule.risk_precision) metricCards.push(<div key="rp" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>{rule.risk_precision}</div></div>);
                    if (rule.no_risk_precision) metricCards.push(<div key="nrp" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>{rule.no_risk_precision}</div></div>);
                    if (rule.risk_recall) metricCards.push(<div key="rr" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>{rule.risk_recall}</div></div>);
                    if (rule.no_risk_recall) metricCards.push(<div key="nrr" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>{rule.no_risk_recall}</div></div>);
                    if (rule.accuracy) metricCards.push(<div key="acc" style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px' }}><div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div><div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>{rule.accuracy}</div></div>);
                    
                    if (metricCards.length === 0) return null;
                    const cols = metricCards.length <= 3 ? metricCards.length : 5;
                    return (
                      <div className="grid gap-3" style={{ fontSize: '13px', gridTemplateColumns: `repeat(${Math.min(cols, metricCards.length)}, 1fr)` }}>
                        {metricCards}
                      </div>
                    );
                  })()}
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    if (rule.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{rule.total}</strong></span>);
                    if (rule.machine_risky !== undefined) stats.push(<span key="mr">机审有风险: <strong style={{ color: '#52c41a' }}>{rule.machine_risky}</strong></span>);
                    if (rule.machine_non_risky !== undefined) stats.push(<span key="mnr">机审无风险: <strong style={{ color: '#1890ff' }}>{rule.machine_non_risky}</strong></span>);
                    if (rule.expected_risky !== undefined) stats.push(<span key="er">人审有风险: <strong style={{ color: '#fa541c' }}>{rule.expected_risky}</strong></span>);
                    if (rule.expected_non_risky !== undefined) stats.push(<span key="enr">人审无风险: <strong style={{ color: '#722ed1' }}>{rule.expected_non_risky}</strong></span>);
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* 自定义维度的实例统计（如变更项等） */}
        {dimensionData.instance_stats && dimensionData.instance_stats.length > 0 && (
          <Card
            title={<span style={{ fontSize: '16px', fontWeight: 'bold' }}>实例详细统计</span>}
            style={{ marginTop: '16px', borderRadius: '12px' }}
          >
            {dimensionData.instance_count && (
              <div style={{
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                padding: '16px 24px',
                borderRadius: '12px',
                marginBottom: '16px',
                color: 'white'
              }}>
                <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '4px' }}>
                  💡 维度说明
                </div>
                <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
                  按实例（如变更项）分组统计各项指标。
                  共 {dimensionData.instance_count} 个实例参与计算。
                </div>
              </div>
            )}
            
            <div className="space-y-4">
              {dimensionData.instance_stats.map((instance, index) => (
                <div 
                  key={instance.instance_code || index}
                  style={{
                    background: '#fafafa',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #f0f0f0'
                  }}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: COLORS[index % COLORS.length]
                      }} />
                      <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                        {instance.instance_name || instance.instance_code}
                      </span>
                    </div>
                    <span style={{ 
                      fontSize: '24px', 
                      fontWeight: 'bold', 
                      color: getColorByRate(instance.accuracy || 0)
                    }}>
                      {instance.accuracy || '-'}
                    </span>
                  </div>
                  
                  {/* 动态展示存在的总数信息 */}
                  {(() => {
                    const stats = [];
                    if (instance.total !== undefined) stats.push(<span key="total">总数: <strong style={{ color: '#262626' }}>{instance.total}</strong></span>);
                    if (instance.machine_risky !== undefined) stats.push(<span key="machine_risky">机审有风险: <strong style={{ color: '#52c41a' }}>{instance.machine_risky}</strong></span>);
                    if (instance.machine_non_risky !== undefined) stats.push(<span key="machine_non_risky">机审无风险: <strong style={{ color: '#1890ff' }}>{instance.machine_non_risky}</strong></span>);
                    if (instance.expected_risky !== undefined) stats.push(<span key="expected_risky">人审有风险: <strong style={{ color: '#fa541c' }}>{instance.expected_risky}</strong></span>);
                    if (instance.expected_non_risky !== undefined) stats.push(<span key="expected_non_risky">人审无风险: <strong style={{ color: '#722ed1' }}>{instance.expected_non_risky}</strong></span>);
                    
                    if (stats.length === 0) return null;
                    return (
                      <div className="flex items-center gap-4 mt-3" style={{ fontSize: '13px', color: '#8c8c8c' }}>
                        {stats}
                      </div>
                    );
                  })()}
                  
                  {/* 实例下的规则平均指标 */}
                  {instance.rule_avg && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #d9d9d9' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#595959', marginBottom: '12px' }}>
                        🎯 规则平均指标
                      </div>
                      <div className="grid grid-cols-5 gap-2" style={{ fontSize: '12px' }}>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                            {instance.rule_avg.risk_precision || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险精确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                            {instance.rule_avg.no_risk_precision || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>有风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#faad14' }}>
                            {instance.rule_avg.risk_recall || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>无风险召回率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#722ed1' }}>
                            {instance.rule_avg.no_risk_recall || '0.00%'}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '8px', background: 'white', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                          <div style={{ color: '#8c8c8c', marginBottom: '4px' }}>准确率</div>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#13c2c2' }}>
                            {instance.rule_avg.accuracy || '0.00%'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* 实例下的规则统计 */}
                  {instance.rule_stats && instance.rule_stats.length > 0 && (
                    <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px dashed #d9d9d9' }}>
                      <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#595959', marginBottom: '12px' }}>
                        📋 规则明细 ({instance.rule_stats.length}条)
                      </div>
                      <div className="space-y-3">
                        {instance.rule_stats.map((rule, ruleIndex) => (
                          <div 
                            key={rule.rule_code || ruleIndex}
                            style={{
                              background: 'white',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '1px solid #f0f0f0'
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div style={{
                                  width: '6px',
                                  height: '6px',
                                  borderRadius: '50%',
                                  background: COLORS[ruleIndex % COLORS.length]
                                }} />
                                <span style={{ fontSize: '14px', fontWeight: 500, color: '#262626' }}>
                                  {rule.rule_name || rule.rule_code}
                                </span>
                              </div>
                              {rule.accuracy && (
                                <span style={{ 
                                  fontSize: '18px', 
                                  fontWeight: 'bold', 
                                  color: getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)
                                }}>
                                  {rule.accuracy}
                                </span>
                              )}
                            </div>
                            {rule.accuracy && (
                              <Progress 
                                percent={parseFloat(rule.accuracy?.replace('%', '') || 0)} 
                                strokeColor={getColorByRate(parseFloat(rule.accuracy?.replace('%', '') || 0) / 100)}
                                trailColor="#f0f0f0"
                                strokeWidth={6}
                                showInfo={false}
                              />
                            )}
                            
                            {/* 动态展示存在的指标 */}
                            {(() => {
                              const metrics = [];
                              if (rule.risk_precision) metrics.push({ label: '有风险精确率', value: rule.risk_precision, color: '#52c41a' });
                              if (rule.no_risk_precision) metrics.push({ label: '无风险精确率', value: rule.no_risk_precision, color: '#1890ff' });
                              if (rule.risk_recall) metrics.push({ label: '有风险召回率', value: rule.risk_recall, color: '#faad14' });
                              if (rule.no_risk_recall) metrics.push({ label: '无风险召回率', value: rule.no_risk_recall, color: '#722ed1' });
                              if (rule.accuracy) metrics.push({ label: '准确率', value: rule.accuracy, color: '#13c2c2' });
                              
                              if (metrics.length === 0) return null;
                              
                              const cols = metrics.length <= 3 ? metrics.length : 5;
                              return (
                                <div className="grid gap-2 mt-2" style={{ 
                                  fontSize: '12px',
                                  gridTemplateColumns: `repeat(${Math.min(cols, metrics.length)}, 1fr)`
                                }}>
                                  {metrics.map((m, i) => (
                                    <div key={i} style={{ textAlign: 'center', padding: '4px' }}>
                                      <div style={{ color: '#8c8c8c', marginBottom: '2px' }}>{m.label}</div>
                                      <div style={{ fontWeight: 'bold', color: m.color }}>{m.value}</div>
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  // 渲染分组指标
  const renderGroupMetrics = (groupMetrics) => {
    if (!groupMetrics || Object.keys(groupMetrics).length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
          📂 分组维度指标
        </h2>
        
        <div style={{
          background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
          padding: '16px 24px',
          borderRadius: '12px',
          marginBottom: '16px',
          color: '#333'
        }}>
          <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            💡 维度说明
          </div>
          <div style={{ fontSize: '13px', lineHeight: '1.6', opacity: 0.85 }}>
            按规则分组（如变更项）统计各项指标，支持多级嵌套分组结构。共 {Object.keys(groupMetrics).length} 个分组。
          </div>
        </div>

        <Row gutter={[16, 16]}>
          {Object.entries(groupMetrics).map(([groupName, groupData], index) => (
            <Col span={12} key={groupName}>
              <Card
                style={{
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  height: '100%'
                }}
                bodyStyle={{ padding: '20px' }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: COLORS[index % COLORS.length]
                  }} />
                  <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                    {groupName}
                  </span>
                </div>
                
                {/* 准确率 */}
                <div className="flex items-center justify-between mb-3">
                  <span style={{ fontSize: '14px', color: '#666' }}>准确率</span>
                  <span style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    color: getColorByRate(groupData.accuracy_value || 0)
                  }}>
                    {groupData.accuracy || '-'}
                  </span>
                </div>
                <Progress 
                  percent={(groupData.accuracy_value || 0) * 100} 
                  strokeColor={getColorByRate(groupData.accuracy_value || 0)}
                  trailColor="#e8e8e8"
                  strokeWidth={8}
                  showInfo={false}
                />
                
                {/* 详细指标 */}
                <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <div style={{ background: '#f6ffed', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: '4px' }}>有风险精确率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.risk_precision || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#e6f7ff', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#1890ff', marginBottom: '4px' }}>无风险精确率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.no_risk_precision || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#fff7e6', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#fa8c16', marginBottom: '4px' }}>有风险召回率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.risk_recall || '-'}</div>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ background: '#f9f0ff', padding: '12px', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#722ed1', marginBottom: '4px' }}>无风险召回率</div>
                        <div style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>{groupData.no_risk_recall || '-'}</div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  };

  // 渲染自定义指标
  const renderCustomMetrics = (customMetrics) => {
    if (!customMetrics || Object.keys(customMetrics).length === 0) return null;

    return (
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
          🔧 自定义指标
        </h2>
        <Row gutter={[16, 16]}>
          {Object.entries(customMetrics).map(([key, metric], index) => {
            const value = metric.value !== undefined ? metric.value : metric;
            const formatted = metric.formatted || (typeof value === 'number' ? `${(value * 100).toFixed(2)}%` : value);
            
            return (
              <Col span={6} key={key}>
                <Card 
                  style={{
                    background: `linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)`,
                    border: 'none',
                    borderRadius: '16px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    height: '100%'
                  }}
                  bodyStyle={{ padding: '24px' }}
                >
                  <div className="flex flex-col h-full">
                    <div className="flex items-start justify-between mb-3">
                      <Tag color="rgba(255,255,255,0.3)" style={{ 
                        border: '1px solid rgba(255,255,255,0.5)',
                        color: 'white',
                        fontWeight: 500
                      }}>
                        自定义
                      </Tag>
                      <ThunderboltOutlined style={{ fontSize: '20px', color: 'rgba(255,255,255,0.8)' }} />
                    </div>
                    
                    <div className="flex-1">
                      <div style={{ color: 'white', fontSize: '14px', marginBottom: '8px', opacity: 0.9 }}>
                        {metric.name || key}
                      </div>
                      <div style={{ color: 'white', fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>
                        {formatted}
                      </div>
                    </div>
                    
                    {typeof value === 'number' && (
                      <div style={{ marginTop: '16px' }}>
                        <Progress 
                          percent={value * 100} 
                          strokeColor="rgba(255,255,255,0.9)"
                          trailColor="rgba(255,255,255,0.2)"
                          showInfo={false}
                          strokeWidth={6}
                        />
                      </div>
                    )}
                  </div>
                </Card>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  // 解析数据结构
  const { presetMetrics, customMetrics, summary } = data;

  return (
    <div className="auto-review-stats-display">
      {title && (
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '24px', color: '#1a1a1a' }}>
          {title}
        </h1>
      )}

      {/* 汇总信息 */}
      {summary && Object.keys(summary).length > 0 && (
        <Card 
          style={{ 
            borderRadius: '12px', 
            marginBottom: '24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            color: 'white'
          }}
          bodyStyle={{ padding: '20px' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <FileTextOutlined style={{ fontSize: '20px' }} />
            <span style={{ fontSize: '16px', fontWeight: 'bold' }}>汇总信息</span>
          </div>
          <div className="flex flex-wrap gap-4">
            {Object.entries(summary).map(([key, value]) => (
              <div key={key} style={{ fontSize: '14px' }}>
                <span style={{ opacity: 0.8 }}>{key}: </span>
                <span style={{ fontWeight: 'bold' }}>{String(value)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 预设指标 */}
      {presetMetrics && (
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '16px', color: '#1a1a1a' }}>
            <SafetyOutlined style={{ marginRight: '8px' }} />
            预设指标
          </h2>

          {/* 单据维度 */}
          {renderDimensionMetrics(presetMetrics.document, 'document', '单据维度')}

          {/* 规则平均维度 */}
          {renderDimensionMetrics(presetMetrics.rule_avg, 'rule_avg', '规则平均维度')}

          {/* 规则明细维度 */}
          {renderDimensionMetrics(presetMetrics.rule_detail, 'rule_detail', '规则明细维度')}

          {/* 自定义维度（如变更项等） */}
          {Object.entries(statsData.presetMetrics || {})
            .filter(([key]) => !['document', 'rule_avg', 'rule_detail'].includes(key))
            .map(([key, value]) => {
              // 检查是否是分组指标（有 instance_stats 或 rule_stats）
              if (value.instance_stats || value.rule_stats) {
                // 当存在预设维度时，使用定义的维度key（而不是instance_name）
                let displayName = key;
                
                // 将技术标识符转换为友好的显示名称
                if (!/[\u4e00-\u9fa5]/.test(key)) {
                  // 如果不包含中文，进行下划线转换
                  displayName = key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, l => l.toUpperCase());
                }
                return renderDimensionMetrics(value, key, displayName);
              }
              // 检查是否是分组指标
              if (typeof value === 'object' && value.accuracy !== undefined) {
                return renderGroupMetrics({ [key]: value });
              }
              return null;
            })}
        </div>
      )}

      {/* 分组指标 */}
      {data.groupMetrics && renderGroupMetrics(data.groupMetrics)}

      {/* 自定义指标 */}
      {customMetrics && renderCustomMetrics(customMetrics)}
    </div>
  );
}

export default AutoReviewStatsDisplay;
